import React, { useState } from 'react';
import '../landing.css';

const APP_STORE_URL = 'https://apps.apple.com/app/id6759902007';

// Agent-facing marketing landing — the root of getpeekr.com. Buyers who arrive
// with a code still join from the strip up top; everything below pitches agents.
export default function LandingScreen({ onJoinWithCode }) {
  const [code, setCode] = useState('');
  const [codeErr, setCodeErr] = useState('');

  const submitCode = (e) => {
    e.preventDefault();
    const clean = code.trim().toUpperCase();
    if (clean.length < 5) { setCodeErr('Showing codes are at least 5 characters'); return; }
    onJoinWithCode(clean);
  };

  return (
    <div className="lp">

      {/* ── Top bar ─────────────────────────────────────────────────────── */}
      <header className="lp-topbar">
        <span className="lp-wordmark">Peekr</span>
        <a className="lp-topcta" href={APP_STORE_URL} target="_blank" rel="noreferrer">
           Get the app
        </a>
      </header>

      {/* ── Buyer strip — joining a showing ─────────────────────────────── */}
      <form className="lp-buyer-strip" onSubmit={submitCode}>
        <span className="lp-buyer-label">Joining a showing?</span>
        <input
          className="lp-code-input"
          type="text"
          placeholder="Enter code · e.g. MAPLE12"
          value={code}
          onChange={(e) => { setCode(e.target.value.toUpperCase()); setCodeErr(''); }}
          maxLength={20}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="characters"
          spellCheck="false"
          aria-label="Showing code"
        />
        <button className="lp-code-btn" type="submit">Join →</button>
        {codeErr && <span className="lp-code-err">{codeErr}</span>}
      </form>

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="lp-hero">
        <div className="lp-hero-copy">
          <div className="lp-eyebrow lp-d1">Peekr for Agents</div>
          <h1 className="lp-h1 lp-d2">
            Close the buyers who <span className="lp-h1-accent">can't be there.</span>
          </h1>
          <p className="lp-sub lp-d3">
            Walk any property live from your iPhone. Your buyer watches in their
            browser — no app, no download. Just send a link.
          </p>
          <div className="lp-hero-ctas lp-d4">
            <a className="lp-store-btn" href={APP_STORE_URL} target="_blank" rel="noreferrer">
              <svg className="lp-store-mark" width="22" height="26" viewBox="0 0 384 512" fill="currentColor" aria-hidden="true"><path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/></svg>
              <span className="lp-store-txt">
                <small>Download on the</small>
                App Store
              </span>
            </a>
            <a className="lp-demo-btn" href="/demo.html">▶ Watch the 2-min demo</a>
            <span className="lp-free-note">Free to start · Pro trial included</span>
          </div>
        </div>

        {/* The product, live — browser mock with brand card + lead toast */}
        <div className="lp-mock lp-d3" aria-hidden="true">
          <div className="lp-mock-chrome">
            <span className="lp-dot" /><span className="lp-dot" /><span className="lp-dot" />
            <span className="lp-mock-url">getpeekr.com/join/MAPLE12</span>
          </div>
          <div className="lp-mock-stage">
            <span className="lp-live-pill"><span className="lp-live-dot" />LIVE</span>
            <div className="lp-mock-sheen" />
            <div className="lp-mock-room" />
            <div className="lp-lead-toast">🎯 New lead — Sarah · (555) 012-3344</div>
          </div>
          <div className="lp-mock-brandcard">
            <span className="lp-avatar">JR</span>
            <span className="lp-agent">
              <b>Jordan Rivera</b>
              <i>Coastline Realty</i>
            </span>
            <span className="lp-pill">📞 Call</span>
            <span className="lp-pill">💬 Text</span>
          </div>
        </div>
      </section>

      {/* ── Trust strip ──────────────────────────────────────────────────── */}
      <div className="lp-strip">
        <span>No app for buyers</span><i>·</i>
        <span>Your brand front and center</span><i>·</i>
        <span>Every viewer becomes a lead</span><i>·</i>
        <span>AI writes the listing</span>
      </div>

      {/* ── Features ─────────────────────────────────────────────────────── */}
      <section className="lp-features">
        {[
          ['🎥', 'Live remote showings', 'Go live and walk the property in real time. Buyers watch from across town or across the country — sharp video, your narration.'],
          ['🏷️', 'Your brand, not ours', 'Headshot, brokerage, and tap-to-call contact on every showing and photo link. To your buyer, it’s your tool.'],
          ['🎯', 'Capture every lead', 'Buyers leave their name and number before they join. Every link you send becomes a lead with one-tap callback.'],
          ['✨', 'AI listing tools', 'MLS-ready descriptions, standout highlights, auto room labels, and hero-shot picks — generated from your photos.'],
        ].map(([icon, title, body]) => (
          <article className="lp-card" key={title}>
            <span className="lp-card-icon">{icon}</span>
            <h3>{title}</h3>
            <p>{body}</p>
          </article>
        ))}
      </section>

      {/* ── How it works ─────────────────────────────────────────────────── */}
      <section className="lp-how">
        <h2 className="lp-h2">Four steps to a showing<br />anywhere on earth</h2>
        <div className="lp-steps">
          {[
            ['Set your brand once', 'Headshot, brokerage, contact. It rides on every link you ever send.'],
            ['Start a showing', 'Go live with the camera, or share listing photos and documents.'],
            ['Send the link', 'Buyers open it in any browser. Name and number first — that’s your lead.'],
            ['Close from anywhere', 'Follow up with one tap, then let AI write the listing copy.'],
          ].map(([t, b], i) => (
            <div className="lp-step" key={t}>
              <span className="lp-step-n">{String(i + 1).padStart(2, '0')}</span>
              <h4>{t}</h4>
              <p>{b}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Pricing teaser ───────────────────────────────────────────────── */}
      <section className="lp-pricing">
        <div className="lp-price-card">
          <span className="lp-price-tier">Free</span>
          <p>Branded photo showings, buyer lead capture, listing gallery links.</p>
        </div>
        <div className="lp-price-card lp-price-pro">
          <span className="lp-price-badge">For working agents</span>
          <span className="lp-price-tier">Pro</span>
          <span className="lp-price-amt">$19.99<i>/mo</i></span>
          <p>Live video showings, unlimited buyers, the full AI listing toolkit. Free trial included.</p>
        </div>
      </section>

      {/* ── Final CTA ────────────────────────────────────────────────────── */}
      <section className="lp-final">
        <h2 className="lp-h2">Your next showing<br />doesn't need a second car.</h2>
        <a className="lp-store-btn" href={APP_STORE_URL} target="_blank" rel="noreferrer">
          <svg className="lp-store-mark" width="22" height="26" viewBox="0 0 384 512" fill="currentColor" aria-hidden="true"><path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/></svg>
          <span className="lp-store-txt">
            <small>Download on the</small>
            App Store
          </span>
        </a>
      </section>

      {/* ── Footer ───────────────────────────────────────────────────────── */}
      <footer className="lp-footer">
        <span>© 2026 Ebenova Solutions</span>
        <nav>
          <a href="/privacy">Privacy</a>
          <a href="/terms">Terms</a>
          <a href="mailto:info@ebenova.net">Contact</a>
        </nav>
      </footer>
    </div>
  );
}
